import React, { useEffect, useState } from "react";
import { Edit, Save, X, RotateCw } from "lucide-react";
import { fetchFeedbacks, updateFeedback } from "../services/feedbacks";
import AdminCardActions from "../components/AdminCardActions";
import CollapsibleMobileCard from "../components/CollapsibleMobileCard";
import FilterPopover from "../components/FilterPopover";
import ToolbarSearch from "../components/ToolbarSearch";

const FeedbackAdmin = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sortBy, setSortBy] = useState("status");
    const [sortDir, setSortDir] = useState("asc");
    const [editingId, setEditingId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [editedData, setEditedData] = useState({
        status: "",
        response: "",
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadFeedbacks();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = feedbacks.filter((f) => {
            const matchesQuery =
                (f.message || "").toString().toLowerCase().includes(q) ||
                (f.response || "").toString().toLowerCase().includes(q) ||
                (f.status || "").toString().toLowerCase().includes(q);
            if (!matchesQuery) return false;
            if (statusFilter !== "all" && String(f.status || "") !== statusFilter) return false;
            return true;
        });
        setFilteredFeedbacks(filtered);
    }, [feedbacks, searchQuery, statusFilter]);

    const loadFeedbacks = async () => {
        try {
            setLoading(true);
            const data = await fetchFeedbacks();
            setFeedbacks(data);
        } catch (error) {
            console.error("Помилка:", error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setExpandedId(item.id);
        setEditedData({
            status: item.status,
            response: item.response || "",
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditedData({ status: "", response: "" });
    };

    const handleSave = async (id) => {
        try {
            await updateFeedback(id, editedData);
            setFeedbacks((prev) =>
                prev.map((item) =>
                    item.id === id ? { ...item, ...editedData } : item
                )
            );
            setEditingId(null);
        } catch (error) {
            alert("❌ " + error.message);
        }
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredFeedbacks) return [];
        const sorted = [...filteredFeedbacks];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            if (typeof valA === "string") {
                valA = valA.toLowerCase();
                valB = valB.toLowerCase();
            }
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });
        return sorted;
    };

    return (
        <div>
            <div className="config-header">
                <div className="header-buttons">
                    <button
                        className="add-btn refresh toolbar-icon-btn"
                        onClick={loadFeedbacks}
                        disabled={loading}
                        title="Оновити"
                        aria-label="Оновити"
                    >
                        <RotateCw size={16} className={loading ? "spin" : ""} />
                    </button>
                    <FilterPopover
                        active={statusFilter !== "all"}
                        title="Статус відгуку"
                        buttonTitle="Фільтр за статусом"
                        onClear={() => setStatusFilter("all")}
                    >
                        <label className="toolbar-filter-field">
                            <span>Статус</span>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="all">Усі</option>
                                <option value="new">new</option>
                                <option value="reviewing">reviewing</option>
                                <option value="closed">closed</option>
                            </select>
                        </label>
                    </FilterPopover>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук відгуків..."
                    />
                </div>
            </div>

            {isMobile ? (
                <div className="config-grid">
                    {filteredFeedbacks.map((item) => (
                        <CollapsibleMobileCard
                            key={item.id}
                            expanded={expandedId === item.id}
                            editing={editingId === item.id}
                            onToggle={() => setExpandedId((cur) => (cur === item.id ? null : item.id))}
                            title={item.message || "Без тексту"}
                            meta={<span>{item.status}</span>}
                            preview={item.response || "Без відповіді"}
                            actions={(
                                <AdminCardActions
                                    editing={editingId === item.id}
                                    onEdit={() => handleEdit(item)}
                                    onSave={() => handleSave(item.id)}
                                    onCancel={handleCancelEdit}
                                />
                            )}
                        >
                            <div>
                                <span className="collapsible-card__field-label">Повідомлення</span>
                                {item.message}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Відповідь</span>
                                {editingId === item.id ? (
                                    <input
                                        type="text"
                                        value={editedData.response}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, response: e.target.value })
                                        }
                                    />
                                ) : (item.response || "—")}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Статус</span>
                                {editingId === item.id ? (
                                    <select
                                        value={editedData.status}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, status: e.target.value })
                                        }
                                    >
                                        <option value="new">new</option>
                                        <option value="reviewing">reviewing</option>
                                        <option value="closed">closed</option>
                                    </select>
                                ) : item.status}
                            </div>
                        </CollapsibleMobileCard>
                    ))}
                </div>
            ) : (
                <div className="table-responsive">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("message")}>
                                    Повідомлення {sortBy === "message" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("response")}>
                                    Відповідь {sortBy === "response" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                                    Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((item) => (
                                <tr key={item.id}>
                                    <td>{item.message}</td>
                                    <td>
                                        {editingId === item.id ? (
                                            <input
                                                type="text"
                                                value={editedData.response}
                                                onChange={(e) =>
                                                    setEditedData({
                                                        ...editedData,
                                                        response: e.target.value,
                                                    })
                                                }
                                            />
                                        ) : (
                                            item.response || "—"
                                        )}
                                    </td>
                                    <td>
                                        {editingId === item.id ? (
                                            <select
                                                value={editedData.status}
                                                onChange={(e) =>
                                                    setEditedData({
                                                        ...editedData,
                                                        status: e.target.value,
                                                    })
                                                }
                                            >
                                                <option value="new">new</option>
                                                <option value="reviewing">reviewing</option>
                                                <option value="closed">closed</option>
                                            </select>
                                        ) : (
                                            item.status
                                        )}
                                    </td>
                                    <td className="actions">
                                        {editingId === item.id ? (
                                            <>
                                                <button
                                                    className="icon-button"
                                                    onClick={() => handleSave(item.id)}
                                                    title="Зберегти"
                                                >
                                                    <Save className="icon-btn" />
                                                </button>
                                                <button
                                                    className="icon-button"
                                                    onClick={handleCancelEdit}
                                                    title="Скасувати"
                                                >
                                                    <X className="icon-btn" />
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                className="icon-button"
                                                onClick={() => handleEdit(item)}
                                                title="Редагувати"
                                            >
                                                <Edit className="icon-btn" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default FeedbackAdmin;
